import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Calendar, Check, X } from 'lucide-react';
import ActionButton from '@/components/base/ActionButton';
import SegmentedTabs from '@/components/base/SegmentedTabs';
import { useTheme } from '@/hooks/useTheme';
import { shiftDayIso } from '@/hooks/useMarketPricesUi';
import { API_BASE_URL, authFetch } from '@/services/api';
import { buildXlsx, downloadXlsx, type XlsxCell } from '@/utils/xlsxLite';
import { indices, dev, type Band } from './pricesData';

/**
 * Вікно «Завантажити в Excel» (РДН / ВДР) — вигляд і елементи керування за макетом /tg; дані — наші.
 * Період: доба · місяць · рік · свій (тиждень прибрано за нарадою 28.10 — його дає «свій період»).
 * Дані беремо ОДНИМ запитом /market/price-forecast/export (≤ 400 діб), а не по запиту на кожну добу.
 *  · cards  — за кожну добу: середньозважена, BASE, PEAK, OFFPEAK — факт і прогноз ліній BASE / EXPRO + «Весь період»;
 *  · hourly — погодинно: факт, обсяг, «як учора», BASE (P10/P50/P90), EXPRO.
 * Прогнози — «заморожені» до подачі заявок на добу (як на сторінці).
 */

type Kind = 'cards' | 'hourly';
type PeriodKey = 'day' | 'month' | 'year' | 'custom';
type Market = 'DAM' | 'IDM';
interface Row { date: string; hour: number; clock: number; actual: number | null; volume: number | null; naive?: number | null;
  base?: Band | null; expro?: Band | null; baseModel?: string | null; exproModel?: string | null }

const MAX_DAYS = 400;
const dmy = (iso: string) => (iso ? iso.split('-').reverse().join('.') : '');
const toDate = (iso: string) => { const [y, m, d] = iso.split('-').map(Number); return new Date(y, m - 1, d); };
const lastOfMonth = (ym: string) => { const [y, m] = ym.split('-').map(Number); return `${ym}-${String(new Date(Date.UTC(y, m, 0)).getUTCDate()).padStart(2, '0')}`; };
const utc = (iso: string) => { const [y, m, d] = iso.split('-').map(Number); return Date.UTC(y, m - 1, d); };
const daysBetween = (a: string, b: string) => Math.round((utc(b) - utc(a)) / 86400000) + 1;

function rangeFor(p: PeriodKey, d: string, min: string | null, max: string | null): [string, string] {
  let from = d, to = d;
  if (p === 'month') { from = `${d.slice(0, 7)}-01`; to = lastOfMonth(d.slice(0, 7)); }
  else if (p === 'year') { from = `${d.slice(0, 4)}-01-01`; to = `${d.slice(0, 4)}-12-31`; }
  if (max && to > max) to = max;
  if (min && from < min) from = min;
  if (from > to) from = to;
  return [from, to];
}

export default function ExcelExportPanel({ kind, market, pageDate, min, max, initialPeriod, onClose }: {
  kind: Kind; market: Market; pageDate: string; min: string | null; max: string | null; initialPeriod: 'day' | 'month'; onClose: () => void;
}) {
  const { t } = useTranslation();
  const { isDark } = useTheme();
  const base = pageDate || max || '';
  const [period, setPeriod] = useState<PeriodKey>(initialPeriod);
  const [cFrom, setCFrom] = useState(() => shiftDayIso(base, -6));
  const [cTo, setCTo] = useState(base);
  const [status, setStatus] = useState<{ s: 'idle' | 'busy' | 'ok' } | { s: 'err'; msg: string }>({ s: 'idle' });

  const [from, to] = useMemo<[string, string]>(() => (period === 'custom' ? [cFrom, cTo] : rangeFor(period, base, min, max)), [period, cFrom, cTo, base, min, max]);
  const mk = market === 'IDM' ? t('pv2_idm') : t('pv2_dam');
  const busy = status.s === 'busy';
  const days = from && to && from <= to ? daysBetween(from, to) : 0;
  const invalid = period === 'custom' && (!from || !to || from > to) ? t('pv2_x_bad_range') : days > MAX_DAYS ? t('pv2_x_too_long', { n: MAX_DAYS }) : '';
  const H = (v: string): XlsxCell => ({ v, s: 'head' });

  const build = (rows: Row[]): { rows: XlsxCell[][]; widths: number[] } => {
    const dam = market === 'DAM';
    if (kind === 'hourly') {
      if (!dam) {
        const out: XlsxCell[][] = [[H(t('pv2_x_date')), H(t('pv2_hour')), H(t('pv2_x_price')), H(t('pv2_row_volume'))]];
        rows.forEach((r) => out.push([{ v: toDate(r.date), s: 'date' }, r.hour, { v: r.actual, s: 'n2' }, { v: r.volume, s: 'n2' }]));
        return { rows: out, widths: [13, 9, 20, 18] };
      }
      const out: XlsxCell[][] = [[H(t('pv2_x_date')), H(t('pv2_hour')), H(t('pv2_x_clock')), H(t('pv2_row_fact')), H(t('pv2_row_volume')),
        H('BASE P50'), H('BASE P10'), H('BASE P90'), H(t('pv2_row_dev_base')), H('EXPRO P50'), H(t('pv2_row_naive')), H(t('pv2_x_model_base')), H(t('pv2_x_model_expro'))]];
      rows.forEach((r) => out.push([{ v: toDate(r.date), s: 'date' }, r.hour, `${String(r.clock).padStart(2, '0')}:00`, { v: r.actual, s: 'n2' }, { v: r.volume, s: 'n2' },
        { v: r.base?.p50 ?? null, s: 'n2' }, { v: r.base?.p10 ?? null, s: 'n2' }, { v: r.base?.p90 ?? null, s: 'n2' }, { v: dev(r.actual, r.base?.p50), s: 'n2' },
        { v: r.expro?.p50 ?? null, s: 'n2' }, { v: r.naive ?? null, s: 'n2' }, r.baseModel ?? '', r.exproModel ?? '']));
      return { rows: out, widths: [13, 9, 9, 18, 16, 14, 14, 14, 18, 14, 16, 14, 14] };
    }
    // cards: показники кожної доби
    const byDay = new Map<string, Row[]>();
    rows.forEach((r) => { if (!byDay.has(r.date)) byDay.set(r.date, []); byDay.get(r.date)!.push(r); });
    const keys = ['weighted', 'base', 'peak', 'offpeak'] as const;
    const labels = [t('pv2_weighted'), 'BASE', 'PEAK', 'OFFPEAK'];
    const head: XlsxCell[] = [H(t('pv2_x_date'))];
    labels.forEach((l) => { head.push(H(`${l}, ${t('pv2_x_fact')}`)); if (dam) head.push(H(`${l}, BASE`), H(`${l}, EXPRO`), H(t('pv2_x_dev_base'))); });
    head.push(H(t('pv2_row_volume')));
    const out: XlsxCell[][] = [head];
    const line = (label: XlsxCell, rs: Row[], bold: boolean) => {
      const n = bold ? 'sumn2' : 'n2';
      const fi = indices(rs.map((r) => ({ clock: r.clock, price: r.actual, volume: r.volume })));
      const bi = dam ? indices(rs.map((r) => ({ clock: r.clock, price: r.base?.p50 ?? null, volume: r.volume }))) : null;
      const ei = dam ? indices(rs.map((r) => ({ clock: r.clock, price: r.expro?.p50 ?? null, volume: r.volume }))) : null;
      const cells: XlsxCell[] = [label];
      keys.forEach((k) => {
        cells.push({ v: fi?.[k] ?? null, s: n });
        if (dam) cells.push({ v: bi?.[k] ?? null, s: n }, { v: ei?.[k] ?? null, s: n }, { v: dev(fi?.[k], bi?.[k]), s: n });
      });
      cells.push({ v: rs.reduce((s, r) => s + (r.volume ?? 0), 0) || null, s: n });
      return cells;
    };
    [...byDay.keys()].sort().forEach((d) => out.push(line({ v: toDate(d), s: 'date' }, byDay.get(d)!, false)));
    if (byDay.size > 1) out.push(line({ v: t('pv2_x_all'), s: 'sum' }, rows, true));
    return { rows: out, widths: [14, ...keys.flatMap(() => (dam ? [18, 14, 14, 16] : [20])), 16] };
  };

  const run = async () => {
    if (invalid || busy) return;
    setStatus({ s: 'busy' });
    try {
      const r = await authFetch(`${API_BASE_URL}/market/price-forecast/export?from=${from}&to=${to}${market === 'IDM' ? '&market=IDM' : ''}`);
      if (!r.ok) throw new Error(String(r.status));
      const j = (await r.json()) as { rows: Row[] };
      const rows = (j.rows ?? []).filter((x) => x.actual != null || x.base?.p50 != null || x.expro?.p50 != null);
      if (!rows.length) { setStatus({ s: 'err', msg: t('pv2_x_nodata', { m: mk }) }); return; }
      const { rows: xr, widths } = build(rows);
      const name = `${market === 'IDM' ? 'vdr' : 'rdn'}_${kind === 'cards' ? 'prices' : 'hourly'}_${from}${to !== from ? `_${to}` : ''}.xlsx`;
      downloadXlsx(name, buildXlsx(kind === 'cards' ? `${mk}` : `${mk} hourly`, xr, widths));
      setStatus({ s: 'ok' });
    } catch {
      setStatus({ s: 'err', msg: t('pv2_x_failed') });
    }
  };

  const dateInput = (value: string, onChange: (v: string) => void) => (
    <div className="flex items-center gap-2 px-2.5" style={{ height: 36, borderRadius: 10, background: 'var(--surface-2)', border: '1px solid var(--line-strong)' }}>
      <Calendar size={14} strokeWidth={2} style={{ color: 'var(--text-muted)', flexShrink: 0 }} />
      <input type="date" value={value} min={min || undefined} max={max || undefined}
        onChange={(e) => { setStatus({ s: 'idle' }); if (e.target.value) onChange(e.target.value); }}
        className="outline-none cursor-pointer tabular-nums"
        style={{ background: 'transparent', border: 'none', color: 'var(--text)', fontSize: 13, fontWeight: 700, colorScheme: isDark ? 'dark' : 'light' }} />
    </div>
  );

  const PERIODS: { key: PeriodKey; label: string; hint: string }[] = [
    { key: 'day', label: t('pv2_x_p_day'), hint: t('pv2_x_h_day') },
    { key: 'month', label: t('pv2_x_p_month'), hint: t('pv2_x_h_month') },
    { key: 'year', label: t('pv2_x_p_year'), hint: t('pv2_x_h_year') },
    { key: 'custom', label: t('pv2_x_p_custom'), hint: '' },
  ];

  return (
    <div role="dialog" aria-label={t('pv2_x_title')}
      style={{ position: 'absolute', right: 0, top: 44, zIndex: 40, width: 560, maxWidth: '92vw', padding: 20, borderRadius: 16,
               background: 'var(--surface-solid)', border: '1px solid var(--line-strong)', boxShadow: '0 18px 48px rgba(0,0,0,0.35)', textAlign: 'left' }}>
      <div className="flex items-start justify-between gap-3">
        <div>
          <div className="font-display text-[18px] font-semibold" style={{ color: 'var(--text)' }}>{t('pv2_x_title')}</div>
          <div className="text-[13px] mt-1" style={{ color: 'var(--text-muted)' }}>
            {kind === 'cards' ? t(market === 'DAM' ? 'pv2_x_sub_cards_dam' : 'pv2_x_sub_cards_idm', { m: mk }) : t(market === 'DAM' ? 'pv2_x_sub_hourly_dam' : 'pv2_x_sub_hourly_idm', { m: mk })}
          </div>
        </div>
        <button type="button" onClick={onClose} aria-label={t('pv2_x_close')} className="shrink-0 cursor-pointer" style={{ color: 'var(--text-muted)' }}><X size={18} strokeWidth={2} /></button>
      </div>
      <div className="text-[11px] font-semibold tracking-wide mt-5 mb-2 uppercase" style={{ color: 'var(--text-muted)' }}>{t('pv2_x_period')}</div>
      <div className="overflow-x-auto">
        <SegmentedTabs items={PERIODS.map((p) => ({ key: p.key, label: p.label }))} activeKey={period}
          onSelect={(k) => { setPeriod(k as PeriodKey); setStatus({ s: 'idle' }); }} />
      </div>
      <div className="flex flex-wrap items-center gap-2 mt-4">
        {period === 'custom' ? (
          <>
            <span className="text-[13px]" style={{ color: 'var(--text-muted)' }}>{t('pv2_x_from')}</span>{dateInput(cFrom, setCFrom)}
            <span className="text-[13px]" style={{ color: 'var(--text-muted)' }}>{t('pv2_x_to')}</span>{dateInput(cTo, setCTo)}
          </>
        ) : (
          <>
            <div className="flex items-center gap-2 px-3 text-[13px] font-bold tabular-nums"
              style={{ height: 36, borderRadius: 10, background: 'var(--surface-2)', border: '1px solid var(--line-strong)', color: 'var(--text)' }}>
              <Calendar size={14} strokeWidth={2} style={{ color: 'var(--text-muted)' }} />
              {from === to ? dmy(from) : `${dmy(from)} – ${dmy(to)}`}
            </div>
            <span className="text-[12px]" style={{ color: 'var(--text-muted)' }}>{PERIODS.find((p) => p.key === period)?.hint}</span>
          </>
        )}
      </div>
      <div className="min-h-[22px] mt-3 text-[13px]">
        {invalid ? <span style={{ color: 'var(--warn)' }}>{invalid}</span>
          : status.s === 'busy' ? <span style={{ color: 'var(--text-muted)' }}>{t('pv2_x_busy', { n: days })}</span>
            : status.s === 'ok' ? <span className="inline-flex items-center gap-1.5" style={{ color: 'var(--ok)' }}><Check size={15} strokeWidth={2.5} />{t('pv2_x_ok')}</span>
              : status.s === 'err' ? <span style={{ color: 'var(--err)' }}>{(status as { msg: string }).msg}</span> : null}
      </div>
      <div className="flex items-center justify-end gap-2 mt-3">
        <ActionButton tone="ghost" onClick={onClose}>{t('pv2_x_cancel')}</ActionButton>
        <ActionButton tone="primary" disabled={!!invalid || busy} onClick={run}>{t('pv2_x_download')}</ActionButton>
      </div>
    </div>
  );
}
